// The composable motion primitive (docs/plans/v2-motion-primitive.md §2).
//
// One track. Everything else is composition.
//
// `entrance: AnimationToken` was a scalar from a closed 12-value enum: one
// animation per element, no exits, no easing, no composition. This file adds
// the primitive UNDERNEATH it — a list of two-stop tracks over independent
// channels — and keeps the 12 tokens as named presets that desugar to it
// (see ./registries/motion-presets.ts).
//
// CRITICAL: nothing here may import from @borradh-workspace/remotion. A
// MotionTrack is data; the renderer owns the interpolation.
export const MOTION_CHANNELS = [
    'opacity',
    'x',
    'y',
    'scale',
    'scaleX',
    'scaleY',
    'rotate',
    'clip',
    'reveal',
    'mask',
    'mask2',
    'mask3',
    'blur',
    'brightness',
];
/** Channels that composite into CSS `filter` rather than `transform`. */
export const MOTION_FILTER_CHANNELS = ['blur', 'brightness'];
/** The mask channels, in the order their parts composite (union). */
export const MOTION_MASK_CHANNELS = ['mask', 'mask2', 'mask3'];
export function isMaskChannel(channel) {
    return MOTION_MASK_CHANNELS.includes(channel);
}
/**
 * Identity value per channel — what the reducer starts from, and what a
 * channel holds when no track touches it.
 */
export const MOTION_CHANNEL_IDENTITY = {
    opacity: 1,
    x: 0,
    y: 0,
    scale: 1,
    scaleX: 1,
    scaleY: 1,
    rotate: 0,
    clip: 1,
    reveal: 1,
    // A mask channel at identity means "fully morphed to `to`". It is inert
    // regardless, because a mask only exists when a track DECLARES a part — an
    // element with no `mask` part emits no mask CSS at all, which is what keeps
    // this addition invisible to all 25 existing templates.
    mask: 1,
    mask2: 1,
    mask3: 1,
    // No blur, and no exposure change. Both must be inert by default or every
    // element in the engine would start emitting a `filter`, which creates a
    // compositing layer per element for nothing.
    blur: 0,
    brightness: 1,
};
export const EASING_REFS = [
    'linear',
    'ease-in',
    'ease-out',
    'ease-in-out',
    'settle',
    'spring-soft',
    'spring-snappy',
    'spring-stamp',
    'spring-pop',
    'spring-firm',
];
/**
 * Spring easings are driven by the RAW frame count and fps, exactly as every
 * spring in v1 and v2 is today — they settle asymptotically rather than over a
 * fixed span. Consequence, and it is a deliberate rule rather than an
 * oversight: **for a spring easing, `MotionTrack.duration` sizes the track's
 * composition window, not its curve.** A 12-frame `slide-up` fades its opacity
 * over 12 frames while its `y` spring keeps settling past frame 12, which is
 * precisely what the current renderer does. Truncating the spring at
 * `duration` would put a visible snap on every slide in the engine.
 */
export const SPRING_EASINGS = [
    'settle',
    'spring-soft',
    'spring-snappy',
    'spring-stamp',
    'spring-pop',
    'spring-firm',
];
export function isSpringEasing(easing) {
    return SPRING_EASINGS.includes(easing);
}
function lerp(from, to, p) {
    return from + (to - from) * p;
}
/**
 * Lerps a mask part's geometry at a 0–1 progress.
 *
 * Lives here rather than in the renderer because it is pure data maths that a
 * test, a lint and a future compile-time preset all want to ask — the same
 * reason `motionAnchorFrame` moved out of the renderer.
 *
 * `progress` is NOT clamped. A track's channel value is `from + (to-from)*p`,
 * so an author who writes `from: 0.2, to: 1` deliberately starts the shape
 * partway; clamping here would silently discard that.
 */
export function interpolateMaskPart(part, progress) {
    if (part.shape === 'ellipse') {
        return {
            shape: 'ellipse',
            ...(part.feather !== undefined ? { feather: part.feather } : {}),
            ...(part.mode !== undefined ? { mode: part.mode } : {}),
            ...(part.lag !== undefined ? { lag: part.lag } : {}),
            progress,
            cx: lerp(part.from.cx ?? 0.5, part.to.cx ?? 0.5, progress),
            cy: lerp(part.from.cy ?? 0.5, part.to.cy ?? 0.5, progress),
            rx: lerp(part.from.rx, part.to.rx, progress),
            ry: lerp(part.from.ry, part.to.ry, progress),
        };
    }
    if (part.shape === 'superellipse') {
        return {
            shape: 'superellipse',
            ...(part.feather !== undefined ? { feather: part.feather } : {}),
            cx: lerp(part.from.cx ?? 0.5, part.to.cx ?? 0.5, progress),
            cy: lerp(part.from.cy ?? 0.5, part.to.cy ?? 0.5, progress),
            rx: lerp(part.from.rx, part.to.rx, progress),
            ry: lerp(part.from.ry, part.to.ry, progress),
            // `n` is lerped LINEARLY, and that is a choice with a visible
            // consequence: perceived "squareness" saturates, so 2->8 spends most of
            // its visible change in the first third and 8->16 is almost invisible.
            // Authoring a morph therefore means picking a modest top exponent, not a
            // large one. Lerping 1/n instead would even that out and is the obvious
            // future change — it is not made here because every preset would shift
            // and none has been re-measured against its source.
            n: lerp(part.from.n ?? 2, part.to.n ?? 2, progress),
        };
    }
    return {
        shape: 'rect',
        ...(part.feather !== undefined ? { feather: part.feather } : {}),
        x: lerp(part.from.x ?? 0, part.to.x ?? 0, progress),
        y: lerp(part.from.y ?? 0, part.to.y ?? 0, progress),
        w: lerp(part.from.w, part.to.w, progress),
        h: lerp(part.from.h, part.to.h, progress),
        // Spread only when AUTHORED, like `feather`/`mode`/`lag` above — so a rect
        // part written before these existed emits the identical object it always
        // did, rather than growing a `rotate: 0, alpha: 1` that every snapshot and
        // equality assertion in the engine would have to be updated for. The
        // renderer applies the identity when they are absent.
        ...(part.from.rotate !== undefined || part.to.rotate !== undefined
            ? { rotate: lerp(part.from.rotate ?? 0, part.to.rotate ?? 0, progress) }
            : {}),
        ...(part.from.alpha !== undefined || part.to.alpha !== undefined
            ? { alpha: lerp(part.from.alpha ?? 1, part.to.alpha ?? 1, progress) }
            : {}),
    };
}
/**
 * Exit sugar. An exit is not a separate mechanism — it is a track with an
 * `{ at: 'end' }` anchor — but 16 v1 layers all want the same 6-frame tail
 * fade and a template should not hand-write it.
 */
export const EXIT_TOKENS = [
    'none',
    'fade-out',
    'slide-out-up',
    'pop-out',
];
// ── Track geometry ──────────────────────────────────────────────────
//
// ONE definition of "where does this track open and how long is it", shared by
// the renderer's reducer, the lint and the compiler's Sequence-extension pass.
// It used to live only inside `packages/remotion/src/registries/motion.ts`,
// which meant the compiler could not ask the question the renderer answers —
// and that gap is exactly how an end-anchored exit fell outside its Sequence
// with no error and no warning.
/** Where a track's window opens, in element-local frames. May be negative. */
export function motionAnchorFrame(track, windowFrames) {
    const anchor = track.anchor;
    if (!anchor || anchor.at === 'start')
        return anchor?.frame ?? 0;
    // 'end' is measured BACKWARDS from the element's own end, which is exactly
    // the `[dur - N, dur]` shape 16 v1 layers hand-write.
    return windowFrames - (anchor.frame ?? 0);
}
/** A track's composition window, in frames. Never below 1. */
export function motionTrackSpan(track, windowFrames) {
    if (track.duration === 'block')
        return Math.max(1, windowFrames);
    return Math.max(1, track.duration);
}
/**
 * The element-local frame a track's window closes at.
 *
 * NOTE for springs: `duration` sizes the composition window, not the curve
 * (see SPRING_EASINGS), so a spring keeps settling past this frame. That is
 * deliberate and unchanged — this is the AUTHORED end, which is the only thing
 * a Sequence can be sized from. A spring's asymptotic tail is already
 * indistinguishable from its resting value long before it matters.
 */
export function motionTrackEndFrame(track, windowFrames) {
    return (motionAnchorFrame(track, windowFrames) +
        motionTrackSpan(track, windowFrames));
}
/**
 * How many frames PAST an element's own window its longest track runs.
 *
 * This is the number the Sequence-extension fix is built on. A block's
 * `Sequence` is sized from `durationInFrames`; an `{ at: 'end', frame: 0 }`
 * exit opens exactly AT that frame and would therefore never paint a single
 * pixel — no error, no warning, the animation simply never plays. Extending
 * the *Sequence* (and never the element's motion window) is what makes it
 * visible without moving anything that already renders.
 *
 * A repeating track has no end, so it contributes nothing: its element's own
 * window is what bounds it.
 */
export function motionOverhangFrames(tracks, windowFrames) {
    let overhang = 0;
    for (const track of tracks) {
        if (track.repeat)
            continue;
        const past = motionTrackEndFrame(track, windowFrames) - windowFrames;
        if (past > overhang)
            overhang = past;
    }
    return Math.ceil(Math.max(0, overhang));
}
/**
 * The live window a ResolvedMotion's anchors resolve against — the element's
 * OWN span, not the block's. Every renderer call site open-codes this; it is
 * here so the compiler computes the identical number.
 */
export function motionWindowFrames(motion, blockDurationInFrames) {
    return (motion.endFrame ?? blockDurationInFrames) - motion.startFrame;
}
/**
 * Frames past the BLOCK's end that this element's motion needs in order to be
 * seen at all. Accounts for the element sitting partway into the block.
 */
export function motionTailFrames(motion, blockDurationInFrames) {
    const window = motionWindowFrames(motion, blockDurationInFrames);
    const overhang = motionOverhangFrames(motion.tracks, window);
    if (overhang === 0)
        return 0;
    const elementEnd = motion.startFrame + window;
    return Math.max(0, elementEnd + overhang - blockDurationInFrames);
}
//# sourceMappingURL=motion.js.map